// Player (init-handoff §4.5). Picks the best advertised tier and degrades down; keeps the last
// frame on `paused` (no teardown); renders a countdown when `scheduled` and a VOD when `ended`.
// The live view layers a danmaku (bullet-comment) overlay fed by the chat socket on top of the
// video, and a full custom control bar (play/volume/PiP/fullscreen/live-edge + danmaku composer).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarClock, Loader2, Volume2 } from "lucide-react";
import { useMe } from "../api/hooks.js";
import type { PlaybackEndpoint, RecordingDTO, StreamStatus } from "../api/types.js";
import { useLoginModal } from "../ui/loginModal.js";
import { nameColor } from "@/lib/nameColor";
import type { ChatLine } from "../ws/useStreamSocket.js";
import { Controls, type DanmakuControls } from "./Controls.js";
import { DanmakuLayer, type DanmakuHandle, type DanmakuItem } from "./danmaku/DanmakuLayer.js";
import { useDanmakuSettings } from "./danmaku/settings.js";
import { FlvPlayer } from "./flv.js";
import { HlsPlayer, playWhenReady } from "./hls.js";
import type { PlayerDriver } from "./types.js";
import { useVideoState } from "./useVideoState.js";
import { WhepPlayer } from "./whep.js";

function driverFor(kind: PlaybackEndpoint["protocol"]): PlayerDriver {
  switch (kind) {
    case "whep":
      return new WhepPlayer();
    case "flv":
      return new FlvPlayer();
    default:
      return new HlsPlayer();
  }
}

function orderEndpoints(eps: PlaybackEndpoint[], lowLatency: boolean): PlaybackEndpoint[] {
  const rank = (p: PlaybackEndpoint): number => {
    const order = lowLatency
      ? { whep: 0, llhls: 1, flv: 2, hls: 3 }
      : { llhls: 0, hls: 1, flv: 2, whep: 3 };
    return order[p.protocol] ?? 9;
  };
  return [...eps].sort((a, b) => rank(a) - rank(b) || a.priority - b.priority);
}

// Auto-hiding controls: visible while the pointer moves, then fades after idle — but stays put
// while paused or while a menu / the danmaku input is engaged (keep-alive).
function useAutoHide(playing: boolean) {
  const [visible, setVisible] = useState(true);
  const keepRef = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  const wake = useCallback(() => {
    setVisible(true);
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      if (!keepRef.current) setVisible(false);
    }, 2800);
  }, []);

  const onMouseLeave = useCallback(() => {
    if (playing && !keepRef.current) setVisible(false);
  }, [playing]);

  const setKeep = useCallback(
    (k: boolean) => {
      keepRef.current = k;
      if (k) {
        setVisible(true);
        window.clearTimeout(timer.current);
      } else {
        wake();
      }
    },
    [wake],
  );

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return { visible: visible || !playing, wake, onMouseLeave, setKeep };
}

function tierLabel(ep: PlaybackEndpoint | null): string | null {
  if (!ep) return null;
  const name = ep.protocol === "whep" ? "WebRTC" : ep.protocol.toUpperCase();
  return ep.latencyHint != null ? `${name} · ${ep.latencyHint}s` : name;
}

export interface PlayerProps {
  status: StreamStatus;
  playbackEndpoints: PlaybackEndpoint[];
  scheduledStartAt: string | null;
  recordings?: RecordingDTO[];
  /** Live chat buffer — newly arriving lines are launched as danmaku. */
  messages?: ChatLine[];
  /** Send a chat line (also a danmaku) from the in-player composer. */
  sendChat?: (body: string) => void;
}

export function Player({ status, playbackEndpoints, scheduledStartAt, recordings, messages, sendChat }: PlayerProps) {
  if (status === "scheduled") return <ScheduledOverlay scheduledStartAt={scheduledStartAt} />;
  if (status === "ended") {
    const ready = (recordings ?? []).find((r) => r.status === "ready" && r.url);
    return <VodPlayer recording={ready} />;
  }
  return (
    <LivePlayer
      status={status}
      playbackEndpoints={playbackEndpoints}
      messages={messages}
      sendChat={sendChat}
    />
  );
}

// ── Live / paused / offline ──
function LivePlayer({
  status,
  playbackEndpoints,
  messages,
  sendChat,
}: {
  status: StreamStatus;
  playbackEndpoints: PlaybackEndpoint[];
  messages?: ChatLine[];
  sendChat?: (body: string) => void;
}) {
  const { t } = useTranslation();
  const { data: me } = useMe();
  const { openLogin } = useLoginModal();
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const driverRef = useRef<PlayerDriver | null>(null);
  const danmakuRef = useRef<DanmakuHandle>(null);
  const [activeTier, setActiveTier] = useState<PlaybackEndpoint | null>(null);
  const [failed, setFailed] = useState(false);
  const [lowLatency, setLowLatency] = useState(() => localStorage.getItem("maitv.lowLatency") !== "0");
  const [danmaku, setDanmaku] = useDanmakuSettings();

  const { state, actions } = useVideoState(videoRef, wrapperRef, { live: true });
  const autohide = useAutoHide(state.playing && status === "live");

  const epsKey = useMemo(() => playbackEndpoints.map((e) => e.url).join("|"), [playbackEndpoints]);

  // Live playback selection + fallback.
  useEffect(() => {
    if (status !== "live") return;
    const video = videoRef.current;
    if (!video) return;
    let cancelled = false;
    const ordered = orderEndpoints(playbackEndpoints, lowLatency);
    setFailed(false);

    (async () => {
      for (const ep of ordered) {
        if (cancelled) return;
        try {
          driverRef.current?.destroy();
          const drv = driverFor(ep.protocol);
          driverRef.current = drv;
          await drv.attach(video, ep, { lowLatency });
          if (cancelled) {
            drv.destroy();
            return;
          }
          setActiveTier(ep);
          return;
        } catch {
          driverRef.current?.destroy();
          driverRef.current = null;
        }
      }
      if (!cancelled) setFailed(true);
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, epsKey, lowLatency]);

  // Tear down only when leaving the live family, keeping the last frame on pause.
  useEffect(() => {
    if (status !== "live") {
      driverRef.current?.destroy();
      driverRef.current = null;
      setActiveTier(null);
    }
  }, [status]);

  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, []);

  // Launch freshly-arrived chat lines as danmaku (skip system + backfilled history).
  const mountTs = useRef(Date.now());
  const emitted = useRef<Set<string>>(new Set());
  useEffect(() => {
    const handle = danmakuRef.current;
    if (!handle || !messages?.length) return;
    const fresh: DanmakuItem[] = [];
    for (const m of messages) {
      if (emitted.current.has(m.id)) continue;
      emitted.current.add(m.id);
      if (m.authorType === "system") continue;
      if (m.ts < mountTs.current - 1500) continue; // older history loaded into the side list only
      fresh.push({
        key: m.id,
        text: m.body,
        color: nameColor(m.id || m.name, m.isStreamer),
        bold: m.isStreamer,
        self: !!me?.id && m.userId === me.id,
      });
    }
    if (fresh.length) handle.emit(fresh);
  }, [messages, me?.id]);

  const toggleLowLatency = () => {
    const next = !lowLatency;
    setLowLatency(next);
    localStorage.setItem("maitv.lowLatency", next ? "1" : "0");
  };

  const danmakuControls: DanmakuControls | undefined = sendChat
    ? {
        settings: danmaku,
        update: setDanmaku,
        canChat: !!me,
        onSend: sendChat,
        onRequireLogin: openLogin,
      }
    : undefined;

  const onKey = (e: React.KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "TEXTAREA") return;
    autohide.wake();
    switch (e.key) {
      case " ":
      case "k":
        e.preventDefault();
        actions.togglePlay();
        break;
      case "m":
        actions.toggleMute();
        break;
      case "f":
        actions.toggleFullscreen();
        break;
      case "d":
        setDanmaku({ enabled: !danmaku.enabled });
        break;
      case "ArrowUp":
        e.preventDefault();
        actions.setVolume(Math.min(1, (state.muted ? 0 : state.volume) + 0.1));
        break;
      case "ArrowDown":
        e.preventDefault();
        actions.setVolume(Math.max(0, (state.muted ? 0 : state.volume) - 0.1));
        break;
    }
  };

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      onMouseMove={autohide.wake}
      onMouseLeave={autohide.onMouseLeave}
      onKeyDown={onKey}
      className="group relative aspect-video w-full overflow-hidden bg-black outline-none"
      style={{ cursor: autohide.visible ? "auto" : "none" }}
    >
      <video
        ref={videoRef}
        className="absolute inset-0 h-full w-full bg-black"
        playsInline
        autoPlay
        muted={state.muted}
        controls={false}
      />

      <DanmakuLayer ref={danmakuRef} settings={danmaku} paused={!state.playing} />

      {/* Click surface: single-click toggles play, double-click toggles fullscreen. */}
      <div
        className="absolute inset-0 z-[5]"
        onClick={() => {
          actions.togglePlay();
          autohide.wake();
        }}
        onDoubleClick={actions.toggleFullscreen}
      />

      {/* Centered unmute affordance for autoplay-muted streams. */}
      {state.muted && state.playing && status === "live" && (
        <button
          onClick={actions.toggleMute}
          className="absolute left-1/2 top-1/2 z-[6] inline-flex -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-full bg-black/65 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm outline-none transition hover:bg-black/80 focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Volume2 className="h-4 w-4" />
          {t("player.unmute")}
        </button>
      )}

      {/* Status overlays */}
      {(status === "paused" || status === "offline" || status === "waiting") && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-black/50 backdrop-blur-[2px]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            {status === "paused" ? t("player.paused") : t("player.offline")}
          </p>
        </div>
      )}

      {status === "live" && state.waiting && state.playing === false && !failed && (
        <div className="absolute inset-0 z-20 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-white/80" />
        </div>
      )}

      {status === "live" && failed && (
        <div className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-3 bg-black/70">
          <p className="text-foreground">{t("player.unavailable")}</p>
          <button
            className="rounded-md border border-white/15 px-4 py-2 text-sm text-primary outline-none transition hover:bg-primary/10 focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => setFailed(false)}
          >
            {t("player.retry")}
          </button>
        </div>
      )}

      <Controls
        mode="live"
        state={state}
        actions={actions}
        visible={autohide.visible}
        onKeepAlive={autohide.setKeep}
        lowLatency={{ value: lowLatency, toggle: toggleLowLatency }}
        tierLabel={tierLabel(activeTier)}
        danmaku={danmakuControls}
      />
    </div>
  );
}

function ScheduledOverlay({ scheduledStartAt }: { scheduledStartAt: string | null }) {
  const { t } = useTranslation();
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  const target = scheduledStartAt ? new Date(scheduledStartAt).getTime() : 0;
  const diff = Math.max(0, target - now);
  const h = Math.floor(diff / 3_600_000);
  const m = Math.floor((diff % 3_600_000) / 60_000);
  const s = Math.floor((diff % 60_000) / 1000);
  return (
    <div className="flex aspect-video w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-card to-background">
      <span className="inline-flex items-center gap-1.5 rounded-full border border-warn/30 bg-warn/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-warn">
        <CalendarClock className="h-3.5 w-3.5" />
        {t("player.scheduledBadge")}
      </span>
      <div className="font-mono text-4xl font-semibold tabular-nums text-foreground sm:text-5xl">
        {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}
      </div>
      <p className="text-sm text-muted-foreground">{t("player.startingSoon")}</p>
    </div>
  );
}

// ── Ended: VOD ──
function VodPlayer({ recording }: { recording?: RecordingDTO }) {
  const { t } = useTranslation();
  if (!recording?.url) {
    return (
      <div className="flex aspect-video w-full items-center justify-center bg-gradient-to-br from-card to-background text-muted-foreground">
        {t("player.ended")}
      </div>
    );
  }
  if (recording.driver === "CLOUDFLARE") {
    const src = recording.url.startsWith("http")
      ? recording.url
      : `https://iframe.cloudflarestream.com/${recording.url}`;
    return <iframe title="VOD" className="aspect-video w-full" src={src} allow="autoplay; fullscreen" />;
  }
  return <VodNativePlayer url={recording.url} />;
}

// Self-hosted recording (m3u8 / mp4) with the full custom control bar (scrub, rate, PiP, fullscreen).
function VodNativePlayer({ url }: { url: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { state, actions } = useVideoState(videoRef, wrapperRef, { live: false });
  const autohide = useAutoHide(state.playing);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (url.endsWith(".m3u8") && !v.canPlayType("application/vnd.apple.mpegurl")) {
      const p = new HlsPlayer();
      void p.attach(v, { protocol: "hls", tier: "standard", url, priority: 0 }, { lowLatency: false });
      return () => p.destroy();
    }
    v.src = url;
    void playWhenReady(v);
  }, [url]);

  return (
    <div
      ref={wrapperRef}
      tabIndex={0}
      onMouseMove={autohide.wake}
      onMouseLeave={autohide.onMouseLeave}
      className="group relative aspect-video w-full overflow-hidden bg-black outline-none"
      style={{ cursor: autohide.visible ? "auto" : "none" }}
    >
      <video ref={videoRef} className="absolute inset-0 h-full w-full bg-black" playsInline controls={false} />
      <div
        className="absolute inset-0 z-[5]"
        onClick={() => {
          actions.togglePlay();
          autohide.wake();
        }}
        onDoubleClick={actions.toggleFullscreen}
      />
      <Controls
        mode="vod"
        state={state}
        actions={actions}
        visible={autohide.visible}
        onKeepAlive={autohide.setKeep}
      />
    </div>
  );
}
